import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";


export interface CowgirlsPromoEvent {
  id: string;
  title: string;
  date: string;
  location: string;
  host_logo_url: string | null;
  description: string | null;
}

const storageKey = (eventId: string) => `cowgirls_promo_seen_${eventId}`;

export const COWGIRLS_PROMO_QUERY_KEY = ["cowgirls-promo-event"] as const;
export const COWGIRLS_PROMO_STALE_TIME = 5 * 60 * 1000;

/**
 * Picks the most relevant Wellness Cowgirls event from a list of
 * active/upcoming candidates. Current rule: the soonest-starting event.
 * Centralized so both the Home banner and Events dialog stay in sync
 * if the relevance rule evolves (e.g., featured flag, distance, etc.).
 */
export const pickMostRelevantCowgirlsEvent = (
  events: CowgirlsPromoEvent[] | null | undefined,
): CowgirlsPromoEvent | null => {
  if (!events || events.length === 0) return null;
  return [...events].sort(
    (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime(),
  )[0];
};

export const fetchCowgirlsPromoEvent = async (): Promise<CowgirlsPromoEvent | null> => {
  const { data, error } = await supabase
    .from("events")
    .select("id, title, date, location, host_logo_url, description")
    .eq("is_cowgirls_hosted", true)
    .eq("is_active", true)
    .gte("date", new Date().toISOString())
    .order("date", { ascending: true })
    .limit(5);
  if (error) throw error;
  return pickMostRelevantCowgirlsEvent(data as CowgirlsPromoEvent[] | null);
};

/**
 * Fetches the next upcoming Wellness Cowgirls-hosted event and decides
 * whether to show the promo dialog (once per event per device).
 * Available to all visitors, including guests.
 */
export const useCowgirlsPromo = () => {
  const [dismissed, setDismissed] = useState(false);

  const { data: event } = useQuery({
    queryKey: COWGIRLS_PROMO_QUERY_KEY,
    enabled: true,
    staleTime: COWGIRLS_PROMO_STALE_TIME,
    queryFn: fetchCowgirlsPromoEvent,
  });

  // Only evaluate localStorage AFTER the event has loaded — prevents any
  // flicker on Events page entry while the query is still resolving.
  let shouldShow = false;
  if (event && !dismissed) {
    let alreadySeen = true;
    try {
      alreadySeen = !!localStorage.getItem(storageKey(event.id));
    } catch {
      alreadySeen = false;
    }
    shouldShow = !alreadySeen;
  }

  const dismiss = () => {
    if (event) {
      try {
        localStorage.setItem(storageKey(event.id), "1");
      } catch {
        // ignore storage errors
      }
    }
    setDismissed(true);
  };

  // Reset dismissed flag when event id changes
  useEffect(() => {
    setDismissed(false);
  }, [event?.id]);

  return { event, shouldShow, dismiss };
};

/**
 * Lightweight variant for the Home page banner: returns the active
 * Wellness Cowgirls event (if any) without any dismissal logic.
 * Shares the React Query cache with useCowgirlsPromo so there are
 * no duplicate network requests.
 */
export const useCowgirlsPromoEvent = () => {
  const { data: event } = useQuery({
    queryKey: COWGIRLS_PROMO_QUERY_KEY,
    enabled: true,
    staleTime: COWGIRLS_PROMO_STALE_TIME,
    queryFn: fetchCowgirlsPromoEvent,
  });

  return { event: event ?? null };
};
